import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription, zip } from 'rxjs';
import { map, skip } from 'rxjs/operators';
import { CategoryDTO } from 'src/app/models/Categories/categoryDTO';
import { ColumnSorterInfo, ConfigScope, ConfigSettingType, DefaultPage, FieldType, FilterEvent, FormatType, PageEvent, SearchEvent, SortEvent, TableConfig, TableEvent } from 'src/app/models/custom-table/custom-table.model';
import { DialogData } from 'src/app/models/dialog/dialog.model';
import { CustomerPartyDTO } from 'src/app/models/Party/customerPartyDTO';
import { PropertyDTO } from 'src/app/models/Properties/propertyDTO';
import { CHIP_STYLE, getChipStyle } from 'src/app/shared/utils/customTableUtils';
import { setCategories, setColumnInfoCategoriesProperties, setProperties } from 'src/app/shared/utils/tableUtils';
import { getCategoriesForListByUsage, getCustomerPartyList, getCustomerTableConfig, getPropertiesForListByUsage, resetPartyState, saveCustomerTableConfig } from 'src/app/store/actions/parties/parties.actions';
import { AppState, getLoadingPartyState, loadedCategoriesListPartyState, loadedCustomerPartyListState, loadedCustomerPartyListTotalState, loadedCustomersTableConfig, loadedPropertiesListPartyState } from 'src/app/store/reducers';

const USAGE_CODE = "SEARCH_CUSTOMER";

@Component({
  selector: 'app-search-customer-dialog',
  templateUrl: './search-customer-dialog.component.html',
  styleUrls: ['./search-customer-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SearchCustomerDialogComponent implements OnInit, OnDestroy {
  public isLoading$: Observable<boolean>;
  public columnInfo$: Observable<ColumnSorterInfo[]>;
  public formPath: string = "customers/customer";
  public headerIcon: string;
  public customerList$: Observable<CustomerPartyDTO[]>;
  public customerList: CustomerPartyDTO[];
  public totalCustomers$: Observable<number>;
  private categories$: Observable<CategoryDTO[]>;
  private properties$: Observable<PropertyDTO[]>;
  private multiSubs: Subscription;
  private loadedCustomersSub: Subscription;
  public selectedRow: object = {};
  public tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.selectCustomer",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Customers.code",
      field: "code",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier: true,
    },
    {
      header: "Customers.customerCode",
      field: "customerCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Customers.Status",
      field: "statusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Customers.vatIN",
      field: "vatin",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Customers.fullName",
      field: "name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Customers.salutation",
      field: "salutationDescription",
      fieldType: FieldType.STRING
    },
    {
      header: "Customers.birthday",
      field: "birthday",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATE,
      hidden: true
    },
    {
      header: "Customers.Phone",
      field: "contacts[PHONE].contact",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.Email",
      field: "contacts[EMAIL].contact",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.Mobile",
      field: "contacts[MOBILE].contact",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.StreetName",
      field: "address.street",
      fieldType: FieldType.STRING,
    },
    {
      header: "Customers.ZipCode",
      field: "address.postalZipCode",
      fieldType: FieldType.STRING,
    },
    {
      header: "Customers.City",
      field: "address.isoCountryCode",
      fieldType: FieldType.STRING,
    },
    {
      header: "Customers.Country",
      field: "address.city",
      fieldType: FieldType.STRING,
    },
    {
      header: "Customers.AddressDesc",
      field: "address.description",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
    {
      header: "Customers.Updated",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private dialogRef: MatDialogRef<SearchCustomerDialogComponent>,
    private store: Store<AppState>,
  ) { }

  ngOnInit(): void {
    this.isLoading$ = this.store.pipe(select(getLoadingPartyState));
    this.customerList$ = this.store.pipe(select(loadedCustomerPartyListState));
    this.totalCustomers$ = this.store.pipe(select(loadedCustomerPartyListTotalState));
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));
    this.columnInfo$ = this.store.pipe(select(loadedCustomersTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return setColumnInfoCategoriesProperties(columnInfo, this.tableConfig);
        }
      })
    );
    this.getCustomerCategoriesProperties();
  }

  public onGetTableData(tableEvent: TableEvent): void {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getCustomersList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  private getCustomersList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ): void {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getCustomerPartyList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  public onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]): void {
    this.store.dispatch(
      saveCustomerTableConfig({
        config: {
          code: USAGE_CODE,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }

  public onConfirmClick(): void {
    this.dialogRef.close(this.selectedRow);
  }

  private getCustomerCategoriesProperties(): void {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig();
      this.getCustomersList();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );

    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  private getTableConfig(): void {
    this.store.dispatch(
      getCustomerTableConfig({
        options: { code: USAGE_CODE, settingType: ConfigSettingType.TABLE_CONFIG },
      })
    );
  }

  private setTableCustomElements(): void {
    this.loadedCustomersSub = this.customerList$
      .pipe(skip(1))
      .subscribe((customers: CustomerPartyDTO[]) => {
        this.customerList = customers;
        if (this.customerList !== undefined && this.customerList !== null && this.customerList?.length > 0) {
          this.customerList = this.customerList.map((customer) => {
            let treatedCustomer = customer;
            treatedCustomer = this.setCustomStatusChip(treatedCustomer);
            return treatedCustomer;
          });
        }
      });
  }

  private setCustomStatusChip(customer: any) {
    const defaultCustomer = {
      ...customer,
    };
    const { statusDescription, statusCode } = customer;
    if (statusDescription === undefined) {
      return defaultCustomer;
    }

    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
    if (statusDescription !== undefined && statusDescription !== null) {
      if (statusCode !== undefined && statusCode !== null) {
        switch (statusCode) {
          case 'ACTIVE':
            style = CHIP_STYLE.SUCCESS;
            break;
          case 'DISABLED':
            style = CHIP_STYLE.INACTIVE;
            break;
          case 'BLOCKED':
            style = CHIP_STYLE.DANGER;
            break;
        }
      }

      let styleSettings = getChipStyle(style);

      let statusChip = {
        ...customer,
        statusDescription: {
          value: statusDescription,
          icon: styleSettings.icon,
          style: styleSettings.style,
        },
      };
      return statusChip;
    }
  }

  public isSelectedRowEmpty(): boolean {
    return Object.keys(this.selectedRow).length === 0;
  }

  public onSelectedRow(event): void {
    const { row, dblClick } = event;
    this.selectedRow = row;
    if (dblClick) {
      this.onConfirmClick();
    }
  }

  ngOnDestroy() {
    if (this.loadedCustomersSub) {
      this.loadedCustomersSub.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    this.store.dispatch(resetPartyState());
  }
}